import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Chip,
  Tooltip,
  alpha,
  useTheme,
  Fade,
  InputAdornment
} from '@mui/material';
import {
  Close,
  Download,
  Delete,
  Pause,
  PlayArrow,
  Search,
  FiberManualRecord,
  Terminal,
  FilterList,
  KeyboardArrowDown
} from '@mui/icons-material';
import { useWebSocketContext } from '../context/WebSocketContext';

export default function LogViewer({ open, onClose, serverName, initialLogs }) {
  const theme = useTheme();
  const [logs, setLogs] = useState([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const [logFilter, setLogFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [paused, setPaused] = useState(false);
  const logsContainerRef = useRef(null);
  const { connected, subscribeLogs, unsubscribeLogs, subscribe } = useWebSocketContext();

  useEffect(() => {
    if (initialLogs) {
      const lines = initialLogs.split('\n').map((line, index) => ({
        id: `initial-${index}`,
        content: line,
        type: line.includes('STDERR') ? 'stderr' : 'stdout',
        timestamp: new Date().toISOString()
      }));
      setLogs(lines);
    }
  }, [initialLogs]);

  useEffect(() => {
    if (!open || !serverName || !connected) return;

    subscribeLogs(serverName);

    const unsubscribe = subscribe('log', (message) => {
      if (message.server !== serverName || paused) return;

      const newLines = message.content.split('\n').filter(line => line.trim()).map((line, index) => ({
        id: `${Date.now()}-${index}`,
        content: line,
        type: message.logType,
        timestamp: message.timestamp
      }));

      setLogs(prev => [...prev.slice(-1000), ...newLines]);
    });

    return () => {
      unsubscribeLogs(serverName);
      unsubscribe();
    };
  }, [open, serverName, connected, subscribeLogs, unsubscribeLogs, subscribe, paused]);

  useEffect(() => {
    if (autoScroll && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const handleScroll = useCallback(() => {
    if (!logsContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isAtBottom);
  }, []);

  const clearLogs = () => {
    setLogs([]);
  };

  const downloadLogs = () => {
    const content = filteredLogs.map(log =>
      `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.content}`
    ).join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${serverName}-logs-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredLogs = logs.filter(log => {
    if (logFilter !== 'all' && log.type !== logFilter) return false;
    if (searchTerm && !log.content.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const highlightSearch = (text) => {
    if (!searchTerm) return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark key={i} style={{
          backgroundColor: '#fbbf24',
          color: '#1a1a2e',
          padding: '0 2px',
          borderRadius: 2
        }}>{part}</mark>
      ) : part
    );
  };

  const scrollToBottom = () => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
      setAutoScroll(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          borderRadius: 4,
          overflow: 'hidden',
          background: '#0d1117',
          border: `1px solid ${alpha('#30363d', 0.8)}`
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        background: 'linear-gradient(180deg, #161b22 0%, #0d1117 100%)',
        borderBottom: `1px solid ${alpha('#30363d', 0.5)}`,
        p: 2.5
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <Terminal sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#e6edf3', fontWeight: 700 }}>
                {serverName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                <Chip
                  icon={<FiberManualRecord sx={{ fontSize: 10, animation: connected ? 'pulse 2s infinite' : 'none' }} />}
                  label={connected ? 'Live' : 'Disconnected'}
                  size="small"
                  sx={{
                    height: 24,
                    background: connected ? alpha('#238636', 0.2) : alpha('#da3633', 0.2),
                    color: connected ? '#3fb950' : '#f85149',
                    border: `1px solid ${connected ? alpha('#238636', 0.4) : alpha('#da3633', 0.4)}`,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    '& .MuiChip-icon': { color: 'inherit' },
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }}
                />
                {paused && (
                  <Chip
                    icon={<Pause sx={{ fontSize: 14 }} />}
                    label="Paused"
                    size="small"
                    sx={{
                      height: 24,
                      background: alpha('#d29922', 0.2),
                      color: '#d29922',
                      border: `1px solid ${alpha('#d29922', 0.4)}`,
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#8b949e',
              '&:hover': {
                background: alpha('#30363d', 0.5),
                color: '#e6edf3'
              }
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Toolbar */}
      <Box sx={{
        px: 2.5,
        py: 1.5,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
        background: '#161b22',
        borderBottom: `1px solid ${alpha('#30363d', 0.5)}`
      }}>
        <ToggleButtonGroup
          value={logFilter}
          exclusive
          onChange={(e, value) => value && setLogFilter(value)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: '#8b949e',
              border: `1px solid ${alpha('#30363d', 0.8)}`,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 2,
              '&.Mui-selected': {
                background: alpha('#667eea', 0.2),
                color: '#667eea',
                borderColor: alpha('#667eea', 0.5),
                '&:hover': {
                  background: alpha('#667eea', 0.3)
                }
              },
              '&:hover': {
                background: alpha('#30363d', 0.5)
              }
            }
          }}
        >
          <ToggleButton value="all">
            <FilterList sx={{ fontSize: 16, mr: 0.5 }} /> All
          </ToggleButton>
          <ToggleButton value="stdout">Stdout</ToggleButton>
          <ToggleButton value="stderr">Stderr</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Search logs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#8b949e', fontSize: 18 }} />
              </InputAdornment>
            )
          }}
          sx={{
            width: 220,
            '& .MuiOutlinedInput-root': {
              background: alpha('#0d1117', 0.8),
              borderRadius: 2,
              color: '#e6edf3',
              fontSize: '0.85rem',
              '& fieldset': {
                borderColor: alpha('#30363d', 0.8)
              },
              '&:hover fieldset': {
                borderColor: alpha('#8b949e', 0.5)
              },
              '&.Mui-focused fieldset': {
                borderColor: '#667eea'
              }
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#8b949e',
              opacity: 1
            }
          }}
        />

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={paused ? 'Resume streaming' : 'Pause streaming'} arrow>
            <IconButton
              onClick={() => setPaused(!paused)}
              sx={{
                background: paused ? alpha('#d29922', 0.15) : alpha('#30363d', 0.5),
                color: paused ? '#d29922' : '#8b949e',
                '&:hover': {
                  background: paused ? alpha('#d29922', 0.25) : alpha('#30363d', 0.8)
                }
              }}
            >
              {paused ? <PlayArrow /> : <Pause />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Clear display" arrow>
            <IconButton
              onClick={clearLogs}
              sx={{
                background: alpha('#30363d', 0.5),
                color: '#8b949e',
                '&:hover': {
                  background: alpha('#da3633', 0.2),
                  color: '#f85149'
                }
              }}
            >
              <Delete />
            </IconButton>
          </Tooltip>

          <Tooltip title="Download logs" arrow>
            <IconButton
              onClick={downloadLogs}
              sx={{
                background: alpha('#30363d', 0.5),
                color: '#8b949e',
                '&:hover': {
                  background: alpha('#238636', 0.2),
                  color: '#3fb950'
                }
              }}
            >
              <Download />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Log Content */}
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <Box
          ref={logsContainerRef}
          onScroll={handleScroll}
          sx={{
            backgroundColor: '#0d1117',
            p: 2,
            fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Monaco, "Courier New", monospace',
            fontSize: '0.8rem',
            lineHeight: 1.7,
            height: '100%',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            '&::-webkit-scrollbar': {
              width: 8
            },
            '&::-webkit-scrollbar-track': {
              background: '#161b22'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#30363d',
              borderRadius: 4,
              '&:hover': {
                background: '#484f58'
              }
            }
          }}
        >
          {filteredLogs.length === 0 ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#8b949e'
            }}>
              <Terminal sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography sx={{ fontFamily: 'inherit', fontSize: '1rem' }}>
                {logs.length === 0 ? 'Waiting for logs...' : 'No logs match the current filter'}
              </Typography>
            </Box>
          ) : (
            filteredLogs.map((log, index) => (
              <Fade in key={log.id} timeout={100}>
                <Box
                  sx={{
                    py: 0.25,
                    px: 1,
                    mx: -1,
                    borderRadius: 1,
                    color: log.type === 'stderr' ? '#f85149' : '#e6edf3',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    '&:hover': {
                      backgroundColor: alpha('#30363d', 0.3)
                    }
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: '#6e7681',
                      fontSize: '0.7rem',
                      fontFamily: 'inherit',
                      minWidth: 40,
                      userSelect: 'none'
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      flex: 1
                    }}
                  >
                    {highlightSearch(log.content)}
                  </Typography>
                </Box>
              </Fade>
            ))
          )}
        </Box>

        {/* Scroll to bottom button */}
        {!autoScroll && filteredLogs.length > 0 && (
          <Fade in>
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 24,
              }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<KeyboardArrowDown />}
                onClick={scrollToBottom}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)'
                  }
                }}
              >
                Scroll to bottom
              </Button>
            </Box>
          </Fade>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{
        justifyContent: 'space-between',
        px: 2.5,
        py: 1.5,
        background: '#161b22',
        borderTop: `1px solid ${alpha('#30363d', 0.5)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ color: '#8b949e', fontWeight: 500 }}>
            {filteredLogs.length.toLocaleString()} lines
            {searchTerm && ` (filtered from ${logs.length.toLocaleString()})`}
          </Typography>
          {autoScroll && (
            <Chip
              icon={<KeyboardArrowDown sx={{ fontSize: 14 }} />}
              label="Auto-scroll"
              size="small"
              sx={{
                height: 22,
                background: alpha('#238636', 0.15),
                color: '#3fb950',
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
          )}
        </Box>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#8b949e',
            '&:hover': {
              background: alpha('#30363d', 0.5),
              color: '#e6edf3'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
